import React, { memo, useEffect } from 'react';
import { Button } from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect } from 'react-router-dom';
import { initPusher } from 'services/pusher';
import { IndividualRecurringOrder } from 'components/admin/orders/recurring-orders/IndividualRecurringOrder';
import { RecurringOrdersPage } from 'components/admin/orders/recurring-orders/RecurringOrdersPage';
import ActiveAssistIcon from '../../assets/images/Icon_Assist_Active.svg';
import InactiveAssistIcon from '../../assets/images/Icon_Assist_Inactive.svg';
import businessInactiveIcon from '../../assets/images/Icon_Businesses_Inactive.svg';
import businessActiveIcon from '../../assets/images/Icon_Businesses_Selected.svg';
// Assets
import customersActiveIcon from '../../assets/images/Icon_Customers_Left_Navigation_Active.svg';
import customersInactiveIcon from '../../assets/images/Icon_Customers_Left_Navigation_Inactive.svg';
import ActiveDevicesIcon from '../../assets/images/Icon_Devices_Active.svg';
import InactiveDevicesIcon from '../../assets/images/Icon_Devices_Inactive.svg';
import ActiveDollarIcon from '../../assets/images/Icon_Dollar_Active.svg';
import InactiveDollarIcon from '../../assets/images/Icon_Dollar_Inactive.svg';
import dashboardActiveIcon from '../../assets/images/Icon_Insights_Active.svg';
import dashboardInactiveIcon from '../../assets/images/Icon_Insights_Inactive.svg';
import InventoryOrdersActiveIcon from '../../assets/images/Icon_Inventory_Active.svg';
import InventoryOrdersInactiveIcon from '../../assets/images/Icon_Inventory_Inactive.svg';
import serviceOrdersActiveIcon from '../../assets/images/Icon_Orders_Left_Navigation_Selected.svg';
import serviceOrdersInactiveIcon from '../../assets/images/Icon_Orders_Left_Navigation_Unselected.svg';
import ActiveRecurringIcon from '../../assets/images/Icon_Recurring_Active.svg';
import InactiveRecurringIcon from '../../assets/images/Icon_Recurring_Inactive.svg';
import scaleDeviceInactiveIcon from '../../assets/images/Icon_Scale_Unselected.svg';
import scaleDeviceActiveIcon from '../../assets/images/Icon_Scale_White.svg';
import ActiveSettingsIcon from '../../assets/images/Icon_Settings_Active.svg';
import InactiveSettingsIcon from '../../assets/images/Icon_Settings_Inactive.svg';
import storesInactiveIcon from '../../assets/images/Icon_Stores_Inactive.svg';
import storesActiveIcon from '../../assets/images/Icon_Stores_Selected.svg';
import { AssistMigrationTable } from '../admin/assist/migration/AssistMigrationTable';
import BusinessesTable from '../admin/businesses/BusinessesTable';
import CreateNewBusiness from '../admin/businesses/CreateNewBusiness';
import IndividualBusinessProfile from '../admin/businesses/IndividualBusinessProfile';
import Communication, {
  COMMUNICATION_CALLS_PATH,
  COMMUNICATION_PATH,
  COMMUNICATION_USAGE_LIMITS_PATH,
} from '../admin/communication/Communication';
import CSVDataEditor from '../admin/customers/CSVDataEditor';
import CentsCustomersTable from '../admin/customers/CentsCustomersTable';
import IndividualCustomerProfile from '../admin/customers/IndividualCustomerProfile';
import UploadBusinessCustomerDeleteOrRestore from '../admin/customers/UploadBusinessCustomerDeleteOrRestore';
import UploadCustomers from '../admin/customers/UploadCustomers';
import DeviceEditor from '../admin/deviceDashboard/deviceEditor/DeviceEditor';
import DeviceTable from '../admin/deviceDashboard/deviceTable/DeviceTable';
import FirmwareManager from '../admin/firmware/FirmwareManager';
import IndividualInventoryOrder from '../admin/orders/inventory-orders/IndividualInventoryOrder';
import InventoryOrdersTable from '../admin/orders/inventory-orders/InventoryOrdersTable';
import IndividualServiceOrder from '../admin/orders/service-orders/IndividualServiceOrder';
import ServiceOrdersTable from '../admin/orders/service-orders/ServiceOrdersTable';
import PaymentDiscrepancyLogDetail from '../admin/paymentDiscrepancyLogs/PaymentDiscrepancyLogDetail';
import PaymentDiscrepancyLogsTable from '../admin/paymentDiscrepancyLogs/PaymentDiscrepancyLogsTable';
import Payment from '../admin/payments/Payment';
import PaymentsTable from '../admin/payments/PaymentsTable';
import IndividualScaleDevice from '../admin/scaleDevices/IndividualScaleDevice';
import RegisterScaleDevice from '../admin/scaleDevices/RegisterScaleDevice';
import ScaleDevicesTable from '../admin/scaleDevices/ScaleDevicesTable';
import CreateNewStore from '../admin/stores/CreateNewStore/CreateNewStore';
import IndividualStoreProfile from '../admin/stores/IndividualStoreProfile';
import StoresTable from '../admin/stores/StoresTable';
import CreateNewUser from '../admin/users/CreateNewUser';
import IndividualUserProfile from '../admin/users/IndividualUserProfile';
import UsersTable from '../admin/users/UsersTable';
import WashboardDeviceDetails from '../admin/washboard/WashboardDeviceDetails';
import WashboardTable from '../admin/washboard/WashboardTable';
// Components
import SideNavigation from '../layout/side-nav/SideNavigation';
import SideNavigationItem from '../layout/side-nav/side-nav-item/SideNavigationItem';

const HomeDashboard = ({ location }) => {
  const currentPath = location.pathname;
  const { oktaAuth } = useOktaAuth();

  useEffect(() => {
    initPusher();
  }, []);

  const onLogoutClick = async () => {
    await oktaAuth.signOut();
  };

  return (
    <>
      <SideNavigation>
        <div>
          <SideNavigationItem
            linkToPath="/businesses"
            currentPath={currentPath}
            label={'Businesses'}
            activeImg={businessActiveIcon}
            inactiveImg={businessInactiveIcon}
          />
          <SideNavigationItem
            linkToPath="/customers"
            currentPath={currentPath}
            label={'Customers'}
            activeImg={customersActiveIcon}
            inactiveImg={customersInactiveIcon}
          />
          <SideNavigationItem
            linkToPath="/home"
            currentPath={currentPath}
            label={'Dashboard'}
            activeImg={dashboardActiveIcon}
            inactiveImg={dashboardInactiveIcon}
          />
          <SideNavigationItem
            linkToPath="/service-orders"
            currentPath={currentPath}
            label={'Service Orders'}
            activeImg={serviceOrdersActiveIcon}
            inactiveImg={serviceOrdersInactiveIcon}
          />
          <SideNavigationItem
            linkToPath="/inventory-orders"
            currentPath={currentPath}
            label={'Inventory Orders'}
            activeImg={InventoryOrdersActiveIcon}
            inactiveImg={InventoryOrdersInactiveIcon}
          />
          <SideNavigationItem
            linkToPath="/recurring-orders"
            currentPath={currentPath}
            label={'Recurring Orders'}
            activeImg={ActiveRecurringIcon}
            inactiveImg={InactiveRecurringIcon}
          />
          <SideNavigationItem
            linkToPath="/payments"
            currentPath={currentPath}
            label="Payments"
            activeImg={ActiveDollarIcon}
            inactiveImg={InactiveDollarIcon}
          />
          <SideNavigationItem
            linkToPath="/payment-discrepancy-logs"
            currentPath={currentPath}
            label="Payment Discrepancies"
            activeImg={ActiveDollarIcon}
            inactiveImg={InactiveDollarIcon}
          />
          <SideNavigationItem
            linkToPath="/scale-devices"
            currentPath={currentPath}
            label={'Scale Devices'}
            activeImg={scaleDeviceActiveIcon}
            inactiveImg={scaleDeviceInactiveIcon}
          />
          <SideNavigationItem
            linkToPath="/stores"
            currentPath={currentPath}
            label={'Stores'}
            activeImg={storesActiveIcon}
            inactiveImg={storesInactiveIcon}
          />
          <SideNavigationItem
            linkToPath="/users"
            currentPath={currentPath}
            label={'Users'}
            activeImg={customersActiveIcon}
            inactiveImg={customersInactiveIcon}
          />
          <SideNavigationItem
            linkToPath="/washboard"
            currentPath={currentPath}
            label={'3.0 Devices'}
            activeImg={ActiveDevicesIcon}
            inactiveImg={InactiveDevicesIcon}
          />
          <SideNavigationItem
            linkToPath="/devices"
            currentPath={currentPath}
            label={'Legacy Devices'}
            activeImg={ActiveDevicesIcon}
            inactiveImg={InactiveDevicesIcon}
          />
          <SideNavigationItem
            linkToPath="/firmware"
            currentPath={currentPath}
            label={'Firmware'}
            activeImg={ActiveDevicesIcon}
            inactiveImg={InactiveDevicesIcon}
          />
          <SideNavigationItem
            linkToPath={COMMUNICATION_PATH}
            currentPath={currentPath}
            label={'Communication'}
            activeImg={ActiveAssistIcon}
            inactiveImg={InactiveAssistIcon}
          />
          <SideNavigationItem
            linkToPath="/business-profiles"
            currentPath={currentPath}
            label={'Business Profiles'}
            activeImg={ActiveSettingsIcon}
            inactiveImg={InactiveSettingsIcon}
          />
        </div>
        <Button
          onClick={onLogoutClick}
          variant="text"
          style={{
            color: 'white',
          }}
        >
          Logout
        </Button>
      </SideNavigation>
      <div className="common-background">
        <div className="dashboard-container">
          <div className="main-area-content">
            <Switch>
              <Route path="/service-orders" component={ServiceOrdersTable} exact />
              <Route path="/recurring-orders" component={RecurringOrdersPage} exact />
              <Route path="/recurring-orders/:id" component={IndividualRecurringOrder} exact />
              <Route path="/service-orders/:id" component={IndividualServiceOrder} exact />
              <Route path="/inventory-orders" component={InventoryOrdersTable} exact />
              <Route path="/inventory-orders/:id" component={IndividualInventoryOrder} exact />
              <Route path="/payments" component={PaymentsTable} exact />
              <Route path="/payments/:id" component={Payment} exact />
              <Route
                path="/payment-discrepancy-logs"
                component={PaymentDiscrepancyLogsTable}
                exact
              />
              <Route
                path="/payment-discrepancy-logs/:id"
                component={PaymentDiscrepancyLogDetail}
                exact
              />
              <Route path="/stores" component={StoresTable} exact />
              <Route path="/stores/new" component={CreateNewStore} exact />
              <Route path="/stores/:id" component={IndividualStoreProfile} exact />
              <Route path="/businesses" component={BusinessesTable} exact />
              <Route path="/businesses/new" component={CreateNewBusiness} exact />
              <Route path="/businesses/:id" component={IndividualBusinessProfile} exact />
              <Route path="/users" component={UsersTable} exact />
              <Route path="/users/new" component={CreateNewUser} exact />
              <Route path="/users/:id" component={IndividualUserProfile} exact />
              <Route path="/scale-devices" component={ScaleDevicesTable} exact />
              <Route path="/scale-devices/new" component={RegisterScaleDevice} exact />
              <Route path="/scale-devices/:id" component={IndividualScaleDevice} exact />
              <Route path="/customers" component={CentsCustomersTable} exact />
              <Route path="/customers/upload" component={UploadCustomers} exact />
              <Route
                path="/customers/delete-restore-upload"
                component={UploadBusinessCustomerDeleteOrRestore}
                exact
              />
              <Route path="/customers/csvdataeditor" component={CSVDataEditor} exact />
              <Route path="/customers/:id" component={IndividualCustomerProfile} exact />
              <Route path="/devices" component={DeviceTable} exact />
              <Route path="/devices/:id" component={DeviceEditor} />
              <Route path="/washboard" component={WashboardTable} exact />
              <Route path="/washboard/:id" component={WashboardDeviceDetails} />
              <Route path="/business-profiles" component={AssistMigrationTable} exact />
              <Route path="/firmware" component={FirmwareManager} exact />
              <Route path="/firmware/admin-settings" component={FirmwareManager} exact />
              <Route
                path={COMMUNICATION_PATH}
                render={() => <Redirect to={COMMUNICATION_CALLS_PATH} />}
                exact
              />
              <Route path={COMMUNICATION_CALLS_PATH} component={Communication} exact />
              <Route path={COMMUNICATION_USAGE_LIMITS_PATH} component={Communication} exact />
              <Route path="/assist" render={() => <Redirect to={COMMUNICATION_CALLS_PATH} />} />
              <Route path="/" render={() => <Redirect to="/businesses" />} />
            </Switch>
          </div>
        </div>
      </div>
    </>
  );
};

HomeDashboard.propTypes = {
  location: PropTypes.object,
};

export default memo(HomeDashboard);
